import React, { useRef } from 'react';
import { useGSAP } from '@gsap/react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

import GirlAlephSvg from '../../assets/Aleph-Girl-Hero.svg?react';

gsap.registerPlugin(ScrollTrigger);

interface HeroSectionProps {
  isDarkMode: boolean;
  onNavigateToSignup?: (source?: string) => void;
}

const HeroSection: React.FC<HeroSectionProps> = ({ isDarkMode, onNavigateToSignup }) => {
  const containerRef = useRef<HTMLDivElement>(null);

  // Text refs
  const headerPart1Ref = useRef<HTMLSpanElement>(null);
  const headerPart2Ref = useRef<HTMLSpanElement>(null);
  const paragraphRef = useRef<HTMLParagraphElement>(null);

  // Visual refs
  const imageRef = useRef<HTMLDivElement>(null);
  const svgContainerRef = useRef<HTMLDivElement>(null);

  // Theme classes
  const headingTextClass = isDarkMode ? 'text-theme-primary-dark' : 'text-blue-700';
  const bodyTextClass = isDarkMode ? 'text-theme-secondary-dark' : 'text-blue-700';
  const svgColorClass = headingTextClass;

  useGSAP(
    () => {
      const ctx = gsap.context(() => {
        // Main Hero Timeline
        const heroTl = gsap.timeline({
          defaults: { ease: 'power3.out' },
        });

        heroTl.from([headerPart1Ref.current, headerPart2Ref.current], {
          y: 100,
          opacity: 0,
          duration: 1.6,
          delay: 0.1,
          stagger: 0.85,
        })
          .from(
            paragraphRef.current,
            {
              y: 50,
              opacity: 0,
              duration: 1,
              delay: 0.4,
            },
            '-=0.8'
          )
          .from(
            imageRef.current,
            {
              x: 150,
              opacity: 0,
              duration: 1,
              delay: 1
            },
            '-=0.8'
          );

        // SVG Animation
        const svg = svgContainerRef.current?.querySelector('svg');
        if (!svg) return;

        const girl = svg.querySelector('#girl');
        const userBubble = svg.querySelector('#user_speech_bubble');
        const userText = svg.querySelector('#user_hi_text');
        const alephBubble = svg.querySelector('#aleph_speech_bubble');
        const alephText = svg.querySelector('#aleph_hi_text');

        const userTargets = [userBubble, userText].filter(Boolean) as Element[];
        const alephTargets = [alephBubble, alephText].filter(Boolean) as Element[];

        // Hide speech bubbles only
        gsap.set([...userTargets, ...alephTargets], { autoAlpha: 0 });
        gsap.set(userTargets, { transformOrigin: 'bottom right' });
        gsap.set(alephTargets, { transformOrigin: 'bottom center' });

        // Ensure girl illustration is visible during landing fade/swipe in
        gsap.set(girl, { autoAlpha: 1 });

        // SVG timeline (only bubbles)
        const bubblesTl = gsap.timeline({
          defaults: { duration: 0.3, ease: 'power2.out' },
          repeat: -1,
          repeatDelay: 6,
        });

        // User speech bubble
        if (userTargets.length) {
          bubblesTl.fromTo(
            userTargets,
            { scale: 0, autoAlpha: 0, y: 0 },
            { scale: 1, autoAlpha: 1, y: -100 },
            '+=0.3'
          );
        }

        // Aleph speech bubble
        if (alephTargets.length) {
          bubblesTl.fromTo(
            alephTargets,
            { scale: 0, autoAlpha: 0, y: 0 },
            { scale: 1, autoAlpha: 1, y: -100 },
            '+=0.9'
          );
        }

        // Start bubble loop after the hero slide finishes
        heroTl.add(bubblesTl);

        // Scroll fade out
        gsap.to(containerRef.current, {
          opacity: 0,
          y: -150,
          scrollTrigger: {
            trigger: containerRef.current,
            start: 'top top',
            end: 'bottom top',
            scrub: true,
          },
        });
      }, containerRef);

      // Strict Mode cleanup
      return () => ctx.revert();
    },
    { scope: containerRef }
  );

  return (
    <section className="w-full flex flex-col px-8 lg:px-16 xl:px-24 pt-38 md:pt-42 h-svh overflow-hidden">
      <div ref={containerRef} className="flex flex-col text-left w-full max-w-full mx-auto h-full">
        {/* Header */}
        <header>
          <h2
            className={`portrait-text-app text-4xl 2xs:text-[2.6rem] xs:text-5xl sm:text-6xl md:text-7xl lg:text-8xl xl:text-8_5xl 3xl:text-9xl font-bold leading-tight -mt-1 ${headingTextClass}`}
          >
            <span ref={headerPart1Ref} className="inline-block whitespace-nowrap">
              Turning books
            </span>
            <br />
            <span ref={headerPart2Ref} className="inline-block">
              into conversations
            </span>
          </h2>
        </header>

        {/* Content row */}
        <div className="flex flex-col 2xl:flex-row pt-10 items-start flex-1 min-h-0">
          {/* Text column */}
          <div className="flex-0 2xl:flex-1 pb-10">
            <p
              ref={paragraphRef}
              className={`leading-relaxed max-w-3xl text-base sm:text-lg md:text-xl xl:text-2xl ${bodyTextClass}`}
            >
              Don’t you ever wish you could stop an audiobook and ask a question? Meet Aleph, a friendly
              English professor in your pocket.
              <span className="block pt-4">
                Sign up is free and only takes a minute, no card details are required.
              </span>
              <button
                type="button"
                onClick={() => onNavigateToSignup?.('hero_section')}
                className={`mt-6 inline-flex cursor-pointer items-center justify-center whitespace-nowrap rounded-full border px-4 py-2 text-base font-medium transition-colors 2xs:px-5 2xs:py-2.5 2xs:text-lg ${
                  isDarkMode
                    ? 'border-blue-600 bg-blue-600 text-white hover:bg-blue-700'
                    : 'border-blue-700 bg-blue-700 text-white hover:bg-blue-800'
                }`}
              >
                Sign Up
              </button>
            </p>
          </div>

          {/* Image / SVG column */}
          <div
            ref={imageRef}
            className="flex w-full flex-1 items-center 2xl:items-end justify-center 2xl:justify-end 2xl:pt-2 h-full overflow-hidden"
          >
            <div ref={svgContainerRef} className="w-full h-full max-h-full max-w-full flex items-end justify-end">
              <GirlAlephSvg
                preserveAspectRatio="xMidYMid meet"
                className={`max-h-full max-w-full w-auto h-auto block ${svgColorClass}`}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
